import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { buildMapStyle } from './mapStyle';
import type { MapBounds } from '../types/entity';

/** Centro inicial: Brasil, com zoom que já mostra o recorte SP (foco do
 *  diferencial rodoviário) sem perder a leitura de "mapa global". */
const INITIAL_CENTER: [number, number] = [-51.9, -14.2];
const INITIAL_ZOOM = 4.2;

export function initMap(container: HTMLElement): maplibregl.Map {
  const map = new maplibregl.Map({
    container,
    style: buildMapStyle(),
    center: INITIAL_CENTER,
    zoom: INITIAL_ZOOM,
    /**
     * CORREÇÃO CRÍTICA (Bug 0 — causa raiz do "HUD sumiu" após a
     * correção anterior do NavigationControl): `customAttribution`
     * como propriedade SOLTA de `MapOptions` foi REMOVIDA no release
     * v4.0.0 do MapLibre GL JS (confirmado: changelog oficial do
     * release, "removed MapOptions.customAttribution" —
     * github.com/maplibre/maplibre-gl-js/releases/tag/v4.0.0). Este
     * projeto está pinado em `^4.7.1` (ver app/package.json), então a
     * propriedade genuinamente não existe no tipo — não era erro de
     * digitação isolado, era código escrito contra a API de uma major
     * anterior (v3.x). O erro TS2353 do `tsc -b` travava o build ANTES
     * do `vite build` rodar, então o site em produção continuava
     * servindo o último build que tinha funcionado — o HUD "sumindo"
     * não era um bug de runtime, era o deploy nunca tendo de fato
     * publicado o código novo.
     *
     * Correção: `customAttribution` vai DENTRO do objeto
     * `attributionControl`, junto de `compact` — confirmado contra a
     * doc oficial (AttributionControlOptions:
     * "optional customAttribution?: string | string[]", dentro do
     * mesmo type que aninha `compact`).
     */
    attributionControl: {
      compact: true,
      customAttribution: 'Webcams: Windy.com',
    },
  });

  /**
   * CORREÇÃO (Bug 2 — organização): NavigationControl agora vive aqui,
   * junto da inicialização do mapa, não solto em main.ts — mantém toda
   * configuração do mapa num único lugar coeso. Assinatura de
   * `addControl(control, position?)` confirmada inalterada na v4
   * (doc oficial: "let nav = new NavigationControl();
   * map.addControl(nav, 'top-left');") — não era fonte do bug, só
   * reorganização.
   */
  map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'bottom-right');

  /**
   * CORREÇÃO (Bug 3): container e canvas do MapLibre dessincronizavam
   * (log real: container=1201x1676, canvas=799x1115) sempre que o
   * container mudava de tamanho por um motivo que não fosse resize da
   * JANELA — troca de orientação do device, abertura/fechamento de
   * bottom sheet empurrando o layout, etc. O evento nativo `resize` da
   * window NÃO dispara nesses casos (o viewport da janela não mudou de
   * tamanho, só o layout interno mudou), então `map.resize()` nunca era
   * chamado e o canvas interno do MapLibre ficava com as dimensões
   * antigas. ResizeObserver observa o próprio elemento container
   * diretamente — cobre qualquer mudança de tamanho dele, seja por
   * resize de janela, rotação, ou mudança de layout (como o bottom
   * sheet mobile abrindo/fechando, que empurra `#map-root` via CSS
   * mesmo sem a janela mudar de tamanho).
   */
  const resizeObserver = new ResizeObserver(() => {
    map.resize();
  });
  resizeObserver.observe(container);

  // Limpa o observer se o mapa for removido (defensivo — este projeto
  // não desmonta o mapa hoje, mas evita leak caso isso mude no futuro).
  map.on('remove', () => {
    resizeObserver.disconnect();
  });

  return map;
}

export function currentMapBounds(map: maplibregl.Map): MapBounds {
  const b = map.getBounds();
  return {
    north: b.getNorth(),
    south: b.getSouth(),
    east: b.getEast(),
    west: b.getWest(),
  };
}

/** Grade sutil de lat/long — puramente visual, via overlay DOM (mais
 *  simples que layer de linhas no MapLibre pra uma grade fixa em tela). */
export function mountLatLonGrid(hudRoot: HTMLElement): void {
  const el = document.createElement('div');
  el.className = 'latlon-grid-overlay';
  hudRoot.appendChild(el);
}
